import { TripAnnotation } from "@fairfleet/geotab";
import ANNOTATION_TRIP_START_OFFSET_MS from "./ANNOTATION_TRIP_START_OFFSET_MS";
import { TripWithAnnotations } from "./getTripsWithAnnotations";

/** An annotation update with the update kind and the new {@link TripAnnotation} data. */
export interface SetAnnotationChange {
  /** The kind of update that should be performed. */
  kind: "add" | "set" | "remove";
  /** The modified annotation data. */
  annotation: Partial<TripAnnotation>;
}

/**
 * Returns an {@link Iterable} yielding the {@link SetAnnotationChange} that should be performed in
 * order for the provided trips to have a single annotation matching the given annotation text.
 *
 * @remarks
 * Trips with multiple annotations should have remove operations returned in order to ensure only
 * one trip annotation exists per trip.
 *
 * Trips with missing annotations have an annotation added.
 *
 * Trips with an annotation not matching the given text are updated.
 *
 * @param text - The annotation text.
 * @param trips - The {@link TripWithAnnotations} to source updates from.
 */
export default function* getSetAnnotationChanges(
  text: string,
  trips: Iterable<TripWithAnnotations>,
): Iterable<SetAnnotationChange> {
  for (const trip of trips) {
    const dateTime = new Date(trip.start?.getTime() + ANNOTATION_TRIP_START_OFFSET_MS);
    const first = trip.annotations[0] ?? null;

    if (!first) {
      yield {
        kind: "add",
        // Geotab only needs the device id on an annotation.
        annotation: { text, device: { id: trip.device.id } as TripAnnotation["device"], dateTime },
      };
    } else if (first.text !== text) {
      yield { kind: "set", annotation: { ...first, text } };
    }

    for (const annotation of trip.annotations.slice(1)) {
      yield { kind: "remove", annotation: { id: annotation.id } };
    }
  }
}
